// TODO kk: we need to split this component into smaller pieces as it is barely maintantable
import { Box, Button, CircularProgress, Drawer, Grid } from "@mui/material"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router"

import InsightsIcon from "@mui/icons-material/Insights"
import ScatterPlotIcon from "@mui/icons-material/ScatterPlot"
import {
  useGetRunGroupByKeyQuery,
  useGetRunResultQuery,
  useLazyGetRunValidationReportQuery,
} from "@store/services/runService"
import { skipToken } from "@reduxjs/toolkit/query/react"
import { useGetModelQuery } from "@store/services/modelsService"
import { ModelAndRunMetadataCard } from "@features/run/viewModel/ModelAndRunMetadataCard"
import { ChartsContainer } from "./ChartsContainer/ChartsContainer"
import {
  useGetGraphDataQuery,
  useGetGraphQuery,
  useGetUserGraphDataQuery,
  useGetUserGraphQuery,
} from "@store/services/graphsService"
import {
  GraphDataViewModel,
  GraphEditMode,
  GraphNodeSelection,
  GroupNodeLabel,
} from "@features/graph/graphViewModel"
import { DrawerComparable } from "./DrawerComparable"
import { GraphContainer } from "@features/graph/GraphContainer"
import { getRunName } from "utils/run"
import { GraphDto, GraphNodeType, UserGraphDto } from "@custom-types/graphs"
import { GraphDetailsContainer } from "@features/graph/details/GraphDetailsContainer"
import { SplitButton } from "@features/results/Result/SplitButton"
import { GraphNodeData, GroupNodeData } from "@store/services/converter/types"
import NodeSelect from "@components/NodeSelect"
import { DrawerMultipleNodes } from "@features/results/Result/DrawerMultipleNodes"
import { Node } from "reactflow"
import { downloadRunResultData } from "@store/download/downloadRunResultsData"
import { enqueueSnackbar } from "notistack"
import { DashboardMode, DrawerDashboard } from "@features/results/Result/DrawerDashboard"
import {
  delDashboardParamUrl,
  hasDashboardParamUrl,
  setDashboardParamUrl,
} from "@features/graph/utils/graphUrl"
import { getLagCorrespondingNode, isLagNodeWithCorrespondingNode } from "@utils/node"
import { useAtomValue } from "jotai"
import { useNodeSelection } from "@features/graph/state/graphAtoms"
import { permissionsAtom } from "@store/authAtoms"
import ShowDashboardButton from "@features/results/ShowDashboardButton"
import SelectRunFromRunGroup, {
  SelectRunFromGroupChangeCallback,
} from "@features/results/Result/SelectRun"
import { ScenarioRun } from "@custom-types/modelRun"
import DOMPurify from "dompurify"
import logger from "@utils/logger"

export default function RawResults() {
  const navigate = useNavigate()
  const { id } = useParams()
  const { data: runData, isFetching: isFetchingRunData } = useGetRunResultQuery(id || "")
  const { data: runModel, isFetching: isFetchingModel } = useGetModelQuery(
    runData?.model_key ?? skipToken,
  )
  const { data: runGroupRuns, isFetching: isFetchingGroupRuns } = useGetRunGroupByKeyQuery(
    runData?.run_group_key ?? skipToken,
  )

  const skipBaseGraph = runData?.user_graph_key || !runData?.base_graph_key
  const skipUserGraph = !runData?.user_graph_key

  const { data: baseGraph, isFetching: isFetchingBaseGraph } = useGetGraphQuery(
    skipBaseGraph ? skipToken : runData?.base_graph_key,
  )
  const { data: userGraph, isFetching: isFetchingUserGraph } = useGetUserGraphQuery(
    skipUserGraph ? skipToken : runData?.user_graph_key,
  )

  const graph: GraphDto | UserGraphDto | undefined = userGraph ?? baseGraph
  const skipUserGraphData = !userGraph?.key
  const skipBaseGraphData = !baseGraph?.key
  const { data: uggraphData } = useGetUserGraphDataQuery(
    skipUserGraphData ? skipToken : userGraph?.key,
  )
  const { data: bgraphData } = useGetGraphDataQuery(skipBaseGraphData ? skipToken : baseGraph?.key)
  const graphData = uggraphData ?? bgraphData

  const permissions = useAtomValue(permissionsAtom)
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false)
  const [graphSelectedNodes, setGraphSelectedNodes] = useState<GraphNodeData[]>([])
  // keep node only for the sidebar (should not affect global selection)
  const [graphNodeForResults, setGraphNodeForResults] = useState<GraphNodeData | null>(null)
  const [graphSelectedGroup, setGraphSelectedGroup] = useState<GroupNodeData | null>(null)
  const [graphSelectedNodesObj, setGraphSelectedNodesObj] = useState<GraphNodeSelection>()
  const [isDrawerNodeVisible, setIsDrawerNodeVisible] = useState(false)
  const [isDrawerCompareVisible, setIsDrawerCompareVisible] = useState(false)
  const [isDrawerDashboardVisible, setIsDrawerDashboardVisible] = useState(false)
  const [isDrawerMultipleNodesVisible, setIsDrawerMultipleNodeVisible] = useState(false)
  const [isBuildRoleAvailable, setIsBuildRoleAvailable] = useState(false)
  const [dashboardSelectedRun, setDashboardSelectedRun] = useState<{
    key: string
    run: ScenarioRun
  }>()
  const [nodeGroupSelectionValue, setNodeGroupSelectionValue] = useState<GroupNodeLabel | null>(
    null,
  )
  const [isDownloadingAllResults, setIsDownloadingAllResults] = useState(false)
  const nodeSelection = useNodeSelection()
  const [fetchLazyGetRunValidationResults, { data: runValidationResultsData, status: statusCode }] =
    useLazyGetRunValidationReportQuery()

  // set dashboard initial selected run based on url param once needed data loads
  useEffect(() => {
    if (!runGroupRuns) return

    const scenarioRunEntry = Object.entries(runGroupRuns.runs).find(([key]) => key === id)
    if (!scenarioRunEntry) return

    const [scenarioRunKey, scenarioRun] = scenarioRunEntry

    setDashboardSelectedRun({ key: scenarioRunKey, run: scenarioRun })
  }, [id, runGroupRuns])

  useEffect(() => {
    setIsBuildRoleAvailable(false)
    if (graph) {
      permissions?.build?.forEach((permission) => {
        permission.families?.forEach((family) => {
          if (family === graph.base_graph_family_key) {
            setIsBuildRoleAvailable(true)
          }
        })
      })
    }
  }, [graph, permissions])

  useEffect(() => {
    const result: GraphNodeData[] = []
    Object.values(nodeSelection).forEach((node) => {
      if (
        node?.data?.display?.label &&
        node?.type !== GraphNodeType.GROUP &&
        node?.type !== GraphNodeType.TABLE
      ) {
        // disable group node dropdown if real node selected
        setNodeGroupSelectionValue(null)

        const selectedNode = GraphDataViewModel.data!.nodeMap[node.id]
        if (!selectedNode) return

        const allNodes = Object.values(GraphDataViewModel.data!.nodeMap)
        const allNodesLabels = allNodes.map((n) => n.display.label)
        let nodeToShowResults = selectedNode

        if (isLagNodeWithCorrespondingNode(nodeToShowResults, allNodesLabels)) {
          const correspondingNode = getLagCorrespondingNode(nodeToShowResults, allNodes)

          if (!correspondingNode) {
            logger.error(
              `Could not find corresponding node for node ${nodeToShowResults.display.label}`,
            )
            return
          }
          nodeToShowResults = correspondingNode
        }

        result.push(nodeToShowResults)
      }
    })
    if (result.length > 0) setGraphSelectedNodes(result)
    setIsDrawerDashboardVisible(hasDashboardParamUrl())
  }, [nodeSelection])

  const isFetching =
    isFetchingRunData ||
    isFetchingBaseGraph ||
    isFetchingUserGraph ||
    isFetchingModel ||
    isFetchingGroupRuns

  const containerRef = useRef(null)

  const selectionChangeCallback = useCallback(
    (selection: GraphNodeSelection) => {
      if (selection.nodes.length === 0) {
        setIsDrawerOpen(false)
        setGraphSelectedGroup(null)
        setGraphNodeForResults(null)
        setNodeGroupSelectionValue(null)
        return
      }

      setGraphSelectedNodesObj(selection)
      setIsDrawerOpen(true)

      const selectedNode = selection.nodes[0]
      if (!selectedNode) return

      // select only one group
      if (selection.nodes.length === 1 && selectedNode.type === GraphNodeType.GROUP) {
        setGraphNodeForResults(null)
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        setGraphSelectedGroup(GraphDataViewModel.data!.groupMap[selectedNode.id])
        return
      }

      if (
        selection.nodes.length === 1 &&
        !(selectedNode.type === GraphNodeType.GROUP || selectedNode.type === GraphNodeType.TABLE)
      ) {
        const graphNode = GraphDataViewModel.data!.nodeMap[selectedNode.id]
        if (!graphNode) return

        const allNodes = Object.values(GraphDataViewModel.data!.nodeMap)
        const allNodesLabels = allNodes.map((n) => n.display.label)
        let nodeToShowResults = graphNode

        if (isLagNodeWithCorrespondingNode(nodeToShowResults, allNodesLabels)) {
          const correspondingNode = getLagCorrespondingNode(nodeToShowResults, allNodes)

          if (!correspondingNode) {
            logger.error(
              `Could not find corresponding node for node ${nodeToShowResults.display.label}`,
            )
            return
          }
          nodeToShowResults = correspondingNode
        }

        setGraphNodeForResults(nodeToShowResults)
        setNodeGroupSelectionValue(null)
        setGraphSelectedGroup(null)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
    [graphData],
  )

  useEffect(() => {
    if (!graphData) return

    if (GraphDataViewModel.groupLabels.length > 0) selectNodeBasedOnUrlHash()
    else GraphDataViewModel.onLoad(selectNodeBasedOnUrlHash)
  }, [graphData])

  const selectNodeBasedOnUrlHash = () => {
    const hash = window.location.hash
    let nodeLabel = hash && hash.split("#")[1]
    // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
    nodeLabel = decodeURIComponent(nodeLabel)
    const nodeToSelect = Object.values(GraphDataViewModel.data!.nodeMap).find(
      (node) => node.display.label === nodeLabel,
    )

    if (nodeToSelect) {
      setGraphSelectedNodes([nodeToSelect])
      setIsDrawerNodeVisible(true)
    }
  }

  const handleChangeNodeSelect = (newValue: GroupNodeLabel | null) => {
    setNodeGroupSelectionValue(newValue)
    if (!newValue) {
      return
    }

    // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
    setGraphSelectedNodes([GraphDataViewModel.data!.nodeMap[newValue.key]])
  }

  if (isFetching) {
    return (
      <Box>
        <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
          <CircularProgress />
        </Box>
      </Box>
    )
  }

  if (!runData || !runModel || !graph || !graphData) {
    return <></>
  }

  const correlationInstruction =
    "Click each node you want to correlate, and then using the node sidebar, choose 'Add to selection'." +
    " Then click 'Show Node Correlations' (this button will be enabled)"
  const resultInstruction = "Select a node in the graph, then click 'Show Node Results'"

  const onShowNodeResult = (node?: Node) => {
    if (!GraphDataViewModel.data) return

    const graphNode = node && GraphDataViewModel.data.nodeMap[node.id]
    const selectedNode = graphNode || graphNodeForResults || graphSelectedNodes[0]

    if (!selectedNode) return

    const allNodes = Object.values(GraphDataViewModel.data.nodeMap)
    const allNodesLabels = allNodes.map((node) => node.display.label)
    let nodeToShowResults = selectedNode

    if (isLagNodeWithCorrespondingNode(nodeToShowResults, allNodesLabels)) {
      const correspondingNode = getLagCorrespondingNode(nodeToShowResults, allNodes)

      if (!correspondingNode) {
        logger.error(
          `Could not find corresponding node for node ${nodeToShowResults.display.label}`,
        )
        return
      }
      nodeToShowResults = correspondingNode
    }

    setGraphNodeForResults(nodeToShowResults)
    navigate({ hash: nodeToShowResults.display.label })
    setIsDrawerNodeVisible(true)
  }

  const handleRunGroupRunChange: SelectRunFromGroupChangeCallback = ({ key }) => {
    navigate(`/Results/Result/${key}${window.location.search}`)
  }

  const handleDashboardRunGroupChange: SelectRunFromGroupChangeCallback = (newValue) => {
    setDashboardSelectedRun(newValue)
  }

  const handleShowDashboardClick = () => {
    if (!runGroupRuns) return

    const scenarioRunEntry = Object.entries(runGroupRuns.runs).find(([key]) => key === id)
    if (!scenarioRunEntry) return

    const [scenarioRunKey, scenarioRun] = scenarioRunEntry

    setIsDrawerDashboardVisible(true)
    setDashboardParamUrl("")
    setDashboardSelectedRun({ key: scenarioRunKey, run: scenarioRun })
  }

  const handleDashboardClose = () => {
    setIsDrawerDashboardVisible(false)
    delDashboardParamUrl()
    setDashboardSelectedRun(undefined)
  }

  const selectedNode =
    graphNodeForResults?.display?.label ||
    graphSelectedNodes[0]?.display?.label ||
    nodeGroupSelectionValue?.label

  return (
    <>
      <Grid
        container
        sx={{ height: "calc(100vh - 64px)", pl: 0 }}
        direction="column"
        wrap="nowrap"
        gap={1}
      >
        <Grid
          item
          xs
          ref={containerRef}
          sx={{ position: "relative", overflow: "initial !important" }}
        >
          <GraphContainer
            graph={graph}
            graphData={graphData}
            showAddToSelection={true}
            isUserGraph={!!userGraph}
            showModified={false}
            showExportGraph={true}
            showResetGraph={false}
            showUserGraphIndicator={true}
            setIsDrawerMultipleNodeVisible={setIsDrawerMultipleNodeVisible}
            selectionChangeCallback={selectionChangeCallback}
            editMode={GraphEditMode.OFF}
            graphFetchingErrorMessage="Preview can't be loaded"
            isMultiselectVisible={true}
          />
          <GraphDetailsContainer
            editMode={GraphEditMode.OFF}
            graphKey={graph?.key}
            selectedNodes={graphSelectedNodesObj}
            isOpen={isDrawerOpen}
            closeCallback={() => {
              setIsDrawerOpen(false)
            }}
            container={containerRef.current}
            isMultiselectVisible={true}
            onShowNodeResultsClick={onShowNodeResult}
            localStoragePrefix="results-page"
          />
        </Grid>
        <Grid container>
          <Grid item xs={12}>
            <ModelAndRunMetadataCard
              model={runModel}
              runName={"Result: Metadata for " + getRunName(runData)}
              samples={runData?.num_samples || 0}
              seed={runData?.seed || 0}
              simulationFrequency={runData?.simulation_frequency}
              version={runData?.version}
              rungroup={runData?.run_group_key}
            />
          </Grid>
          {graphSelectedGroup && (
            <Grid item sx={{ mt: "3px" }} xs={3}>
              <NodeSelect
                sx={{ width: "auto" }}
                graph={graphData}
                value={nodeGroupSelectionValue}
                onChange={handleChangeNodeSelect}
                limitToGroupNodes={[graphSelectedGroup.id]}
              />
            </Grid>
          )}
          <Grid item sx={{ mt: "5px" }} xs={graphSelectedGroup ? 9 : 12}>
            <Grid container spacing={1}>
              <Grid item>
                <SelectRunFromRunGroup
                  runKey={runData.run_group_key}
                  selectedRunName={runData.run_name ?? ""}
                  onChange={handleRunGroupRunChange}
                />
              </Grid>
              {graph.dashboard_s3_location && (
                <Grid item>
                  <ShowDashboardButton onClick={handleShowDashboardClick} />
                </Grid>
              )}
              <Grid item>
                <SplitButton
                  sx={{ marginLeft: 0 }}
                  title={
                    <>
                      Show node results
                      <InsightsIcon />
                    </>
                  }
                  instruction={resultInstruction}
                  properties={{
                    disabled:
                      Object.values(nodeSelection).length !== 1 &&
                      !graphNodeForResults &&
                      !nodeGroupSelectionValue,
                    onClick: () => {
                      onShowNodeResult()
                    },
                  }}
                />
              </Grid>
              <Grid item>
                <SplitButton
                  title={
                    <>
                      Show node correlations <ScatterPlotIcon />{" "}
                    </>
                  }
                  instruction={correlationInstruction}
                  sx={{ marginLeft: 0 }}
                  properties={{
                    disabled:
                      Object.values(nodeSelection).length < 2 ||
                      Object.values(nodeSelection).length > 4,
                    onClick: () => {
                      setIsDrawerCompareVisible(true)
                    },
                  }}
                />
              </Grid>

              <Grid item>
                {runData.extra?.can_user_download_results && (
                  <Button
                    disabled={isDownloadingAllResults}
                    variant="outlined"
                    endIcon={isDownloadingAllResults && <CircularProgress size={14} />}
                    onClick={async () => {
                      if (!id) return
                      try {
                        setIsDownloadingAllResults(true)
                        await downloadRunResultData(id)
                      } catch (e) {
                        enqueueSnackbar(`${(e as Error).message}`, { variant: "error" })
                      } finally {
                        setIsDownloadingAllResults(false)
                      }
                    }}
                  >
                    Download All Results
                  </Button>
                )}
              </Grid>
              {runData.post_validate && isBuildRoleAvailable && (
                <Grid item>
                  <Button
                    variant="outlined"
                    disabled={statusCode == "rejected"}
                    onClick={async () => {
                      if (!id) return
                      const result = runValidationResultsData
                        ? runValidationResultsData
                        : await fetchLazyGetRunValidationResults(id).unwrap()
                      const win = window.open(
                        "",
                        "Validation Results",
                        "toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=yes",
                      )
                      if (!win) return
                      win.document.body.innerHTML = DOMPurify.sanitize(result)
                    }}
                  >
                    {statusCode != "rejected" ? "See Validation Results" : "Error"}
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Drawer
        open={isDrawerNodeVisible}
        anchor="right"
        hideBackdrop={true}
        PaperProps={{
          sx: { width: "95vw" },
        }}
        ModalProps={{
          slots: { backdrop: "div" },
          slotProps: {
            root: {
              style: {
                position: "absolute",
                top: "unset",
                bottom: "unset",
                left: "unset",
                right: "unset",
              },
            },
          },
        }}
      >
        <Box sx={{ m: 2 }}>
          <Button
            onClick={() => {
              setIsDrawerNodeVisible(false)
              if (window.location.hash) {
                const { pathname, search } = window.location
                window.history.replaceState(null, "", `${pathname}${search}`)
              }
            }}
            variant="contained"
            sx={{
              width: "100px",
              position: "fixed",
              top: "12px",
              right: "24px",
              zIndex: 1000,
            }}
          >
            Close
          </Button>
          {selectedNode && <ChartsContainer runs={[runData]} selectedNode={selectedNode} />}
        </Box>
      </Drawer>
      <Drawer
        open={isDrawerCompareVisible}
        anchor="left"
        hideBackdrop={true}
        PaperProps={{
          sx: { width: "95vw" },
        }}
        ModalProps={{
          slots: { backdrop: "div" },
          slotProps: {
            root: {
              style: {
                position: "absolute",
                top: "unset",
                bottom: "unset",
                left: "unset",
                right: "unset",
              },
            },
          },
        }}
      >
        <DrawerComparable
          runId={id || ""}
          selectedNodes={graphSelectedNodes.map((node) => node.display.label)}
          onClose={() => setIsDrawerCompareVisible(false)}
          version={runData?.version}
        />
      </Drawer>
      <Drawer
        open={isDrawerMultipleNodesVisible}
        anchor="left"
        hideBackdrop={true}
        PaperProps={{
          sx: { width: "95vw" },
        }}
        ModalProps={{
          slots: { backdrop: "div" },
          slotProps: {
            root: {
              style: {
                position: "absolute",
                top: "unset",
                bottom: "unset",
                left: "unset",
                right: "unset",
              },
            },
          },
        }}
      >
        <DrawerMultipleNodes
          graphData={graphData}
          selectedNodesData={graphSelectedNodes}
          onClose={() => setIsDrawerMultipleNodeVisible(false)}
          run={runData}
        />
      </Drawer>
      <Drawer
        open={isDrawerDashboardVisible}
        anchor="right"
        PaperProps={{
          sx: { width: "100vw" },
        }}
      >
        <DrawerDashboard
          runKeys={dashboardSelectedRun?.key ? [dashboardSelectedRun.key] : []}
          selectedRunKey={dashboardSelectedRun?.key ?? ""}
          mode={DashboardMode.SingleResult}
          onClose={handleDashboardClose}
          slots={{
            postTitle: (
              <>
                <SelectRunFromRunGroup
                  runKey={runData.run_group_key}
                  selectedRunName={dashboardSelectedRun?.run.scenario_name ?? ""}
                  onChange={handleDashboardRunGroupChange}
                />
              </>
            ),
          }}
        />
      </Drawer>
    </>
  )
}
